import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { ResourceModule } from '../../features/common/ResourceModule';
import { registerForms } from '../../features/common/formRegistry';
import { TabbedPage } from '../../features/common/TabbedPage';
import {
  animalPurchasesApi, animalsApi, calfRecordsApi, expenseCategoriesApi, expensesApi,
  purchaseOrdersApi, purchasesApi, salesApi, suppliersApi,
} from '../../services/resources';
import { formatCurrency, formatDate, today } from '../../utils/format';
import { ILMSStatusBadge } from '../../components/ILMSStatusBadge';
import { cn } from '../../utils/cn';
import type {
  AnimalPurchase, CalfRecord, Expense, ExpenseCategory, ListQueryParams,
  Purchase, PurchaseOrder, Sale, Supplier,
} from '../../types';
import type { FieldConfig, ModuleConfig } from '../../features/common/types';

// Procurement, Sales and Finance were previously three separate nav
// entries/pages, but "buying something" and "recording what it cost" are
// the same real-world action — a Purchases screen that couldn't record an
// animal purchase, next to a Finance screen with its own separate Animal
// Purchases record, was the exact duplicate-flow confusion reported
// (buying a cow could be typed into either the generic Purchases tab under
// Type=Animal, going nowhere useful, or the real Animal Purchases tab under
// Finance). Merged into one page/nav item so there's exactly one place for
// every purchase type, Animal included, and Sales/Expenses sit right beside
// what feeds them (Suppliers/Customers/Expense Categories).
//
// Tab order follows the data flow: buying (Suppliers -> Purchase Orders ->
// Purchases), then spending (Expense Categories -> Expenses), then other
// sales. Milk customers, plans, delivery changes and their bills moved to
// the Online Shop & Customers page (pages/shop/OnlineShopPage.tsx).

const activeBadge = (active: boolean) => (
  <span className={cn('inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full border',
    active ? 'bg-success-50 text-success-700 border-success-100' : 'bg-surface-muted text-ink-500 border-ink-300/30')}>
    {active ? 'Active' : 'Inactive'}
  </span>
);

const suppliersConfig: ModuleConfig<Supplier> = {
  title: 'Suppliers', entityLabel: 'supplier', module: 'Purchases', queryKey: 'suppliers', api: suppliersApi, idKey: 'id',
  defaultValues: { active: true },
  columns: [
    { key: 'name', header: 'Name', primary: true, render: (r) => r.name },
    { key: 'contact', header: 'Contact', render: (r) => r.contact || '—' },
    { key: 'active', header: 'Status', render: (r) => activeBadge(r.active) },
  ],
  fields: [
    { name: 'name', label: 'Name', type: 'text', section: 'Details', required: true },
    { name: 'contact', label: 'Contact', type: 'text', section: 'Details', optional: true },
    { name: 'tax_id', label: 'Tax ID', type: 'text', section: 'Details', optional: true },
    { name: 'address', label: 'Address', type: 'textarea', section: 'Details', optional: true, fullWidth: true },
    { name: 'active', label: 'Active', type: 'checkbox', section: 'Details' },
  ],
};

// Mirrors the backend's _STOCK_TYPES exactly (app/api/v1/procurement.py) —
// Feed/Medicine have a real master list to pick from; Equipment has no
// master table yet (P2, out of scope) so it keeps free-text description only.
const itemRefOptionsKey = (values: Record<string, unknown>) =>
  values.item_type === 'Feed' ? 'feedItems' : values.item_type === 'Medicine' ? 'medicines' : undefined;
const purchaseItemRefOptionsKey = (values: Record<string, unknown>) =>
  values.purchase_type === 'Feed' ? 'feedItems' : values.purchase_type === 'Medicine' ? 'medicines' : undefined;
const isAnimalPurchase = (values: Record<string, unknown>) => values.purchase_type === 'Animal';
const isCalfPurchase = (values: Record<string, unknown>) => values.purchase_type === 'Calf';
// A stock purchase (Feed/Medicine/Equipment/Other) - neither an animal nor a calf.
const isNotAnimalPurchase = (values: Record<string, unknown>) => !isAnimalPurchase(values) && !isCalfPurchase(values);
// Description/Quantity/Unit Price live inside the Purchase's items[] on the
// backend, not as flat top-level fields — so an existing row being edited
// never has them pre-filled (openEdit only spreads the row's own top-level
// keys). Required only on create, so editing an existing non-Animal
// purchase (to change its Payment Status, the only thing edit supports
// today) doesn't get blocked by fields that can never be pre-filled.
const isCreatingNonAnimalPurchase = (values: Record<string, unknown>) => isNotAnimalPurchase(values) && values.id === undefined;

const purchaseOrdersConfig: ModuleConfig<PurchaseOrder> = {
  title: 'Purchase Orders', entityLabel: 'purchase order', module: 'Purchases', queryKey: 'purchase-orders',
  referenceKeys: ['farms', 'suppliers', 'feedItems', 'medicines'],
  api: {
    list: purchaseOrdersApi.list,
    create: (v: any) => purchaseOrdersApi.create({
      farm_id: v.farm_id, po_number: v.po_number, supplier_id: v.supplier_id,
      po_date: v.po_date, expected_delivery_date: v.expected_delivery_date || undefined,
      items: [{
        item_type: v.item_type, item_ref_id: v.item_ref_id || undefined,
        description: v.description, quantity: Number(v.quantity), unit_price: Number(v.unit_price),
      }],
    }),
    update: (id: string) => purchaseOrdersApi.updateStatus(id, 'Approved'),
  },
  idKey: 'id',
  defaultValues: { po_date: today(), item_type: 'Feed' } as unknown as Partial<PurchaseOrder>,
  columns: [
    { key: 'po_number', header: 'PO Number', primary: true, render: (r) => r.po_number },
    { key: 'date', header: 'Date', render: (r) => formatDate(r.po_date) },
    { key: 'total', header: 'Total', align: 'right', render: (r) => formatCurrency(r.total_amount) },
    { key: 'status', header: 'Status', render: (r) => <ILMSStatusBadge status={r.status} size="sm" /> },
  ],
  fields: [
    { name: 'farm_id', label: 'Farm', type: 'select', section: 'Order', required: true, optionsKey: 'farms' },
    { name: 'po_number', label: 'PO Number', type: 'text', section: 'Order', required: true },
    { name: 'supplier_id', label: 'Supplier', type: 'select', section: 'Order', required: true, optionsKey: 'suppliers' },
    { name: 'po_date', label: 'Date', type: 'date', section: 'Order', required: true },
    { name: 'expected_delivery_date', label: 'Expected Delivery', type: 'date', section: 'Order', optional: true },
    { name: 'item_type', label: 'Item Type', type: 'select', section: 'Item', required: true, staticOptions: [
      { value: 'Feed', label: 'Feed' }, { value: 'Medicine', label: 'Medicine' },
      { value: 'Equipment', label: 'Equipment' }, { value: 'Other', label: 'Other' },
    ], helperText: 'Ordering an animal? Record it under Purchases (Type = Animal) instead — Purchase Orders are for stock/equipment deliveries.' },
    { name: 'item_ref_id', label: 'Item', type: 'select', section: 'Item', optional: true, optionsKey: itemRefOptionsKey,
      visibleWhen: (v) => v.item_type === 'Feed' || v.item_type === 'Medicine',
      helperText: 'Which Feed Item / Medicine this line is — links the PO to Inventory so receiving it later updates stock.' },
    { name: 'description', label: 'Description', type: 'text', section: 'Item', required: true },
    { name: 'quantity', label: 'Quantity', type: 'decimal', section: 'Item', required: true, min: 0 },
    { name: 'unit_price', label: 'Unit Price', type: 'decimal', section: 'Item', required: true, min: 0 },
  ] as FieldConfig<any>[],
};

// Purchases is the one point of entry for every kind of purchase, Animal
// included. Type=Animal doesn't create a generic Purchase row at all — it
// creates a real Animal_Purchase record (the same one the animal's own
// profile reads its Acquisition Cost from), while every other Type still
// creates a generic Purchase the way it always has. list()/create()/
// update() below merge/route between the two backend tables so both kinds
// of purchase show up side by side in one table instead of two screens.
// _reference/_supplierName are resolved once at list() time (what was
// bought, who it was bought from — as text, not raw IDs) so the table is
// scannable without opening every row; not real backend fields.
type UnifiedPurchase = (Purchase & { kind: 'Purchase'; _reference: string; _supplierName: string })
  | (AnimalPurchase & { kind: 'AnimalPurchase'; purchase_type: 'Animal'; _reference: string; _supplierName: string })
  // A bought calf (free or at a cost) - stored as a Calf Record (Dairy → Calf
  // Records), since a calf isn't registered as an Animal until it grows up.
  | (CalfRecord & { kind: 'CalfPurchase'; purchase_type: 'Calf'; purchase_date: string; _reference: string; _supplierName: string });

const purchasesConfig: ModuleConfig<UnifiedPurchase> = {
  title: 'Purchases', entityLabel: 'purchase', module: 'Purchases', queryKey: 'purchases',
  referenceKeys: ['farms', 'suppliers', 'feedItems', 'medicines', 'inventoryLocations', 'animals'],
  api: {
    list: async (params?: ListQueryParams) => {
      // Neither /purchases nor /animal-purchases supports free-text search
      // server-side — fetch a generous page of each, merge, then filter/
      // paginate here. Fine at this farm's real data volume; a heavier
      // deployment would want a real merged backend endpoint instead.
      const [purchasesPage, animalPurchasesPage, suppliersPage, animalsPage, calvesPage] = await Promise.all([
        purchasesApi.list({ page: 1, page_size: 500 }),
        animalPurchasesApi.list({ page: 1, page_size: 500 }),
        suppliersApi.list({ page: 1, page_size: 500 }),
        animalsApi.list({ page: 1, page_size: 1000 }),
        // Needs Dairy access - without it, bought calves just aren't listed here.
        calfRecordsApi.list({ page: 1, page_size: 500, source: 'Purchased' }).catch(() => ({ items: [] as CalfRecord[] })),
      ]);
      const supplierName = new Map(suppliersPage.items.map((s) => [s.id, s.name]));
      const animalLabel = new Map(animalsPage.items.map((a) => [a.id, `${a.animal_id} — ${a.animal_name ?? a.animal_type_name ?? 'Animal'}`]));
      const merged: UnifiedPurchase[] = [
        ...purchasesPage.items.map((p) => ({
          ...p, kind: 'Purchase' as const,
          _reference: p.items?.[0]?.description || '—',
          _supplierName: (p.supplier_id && supplierName.get(p.supplier_id)) || '—',
        })),
        ...animalPurchasesPage.items.map((p) => ({
          ...p, kind: 'AnimalPurchase' as const, purchase_type: 'Animal' as const,
          _reference: animalLabel.get(p.animal_id) || '—',
          _supplierName: (p.supplier_id && supplierName.get(p.supplier_id)) || '—',
        })),
        ...calvesPage.items.map((c) => ({
          ...c, kind: 'CalfPurchase' as const, purchase_type: 'Calf' as const, purchase_date: c.purchase_date ?? c.birth_date,
          _reference: `Calf (${c.calf_gender ?? '—'})${c.calf_tag_no ? ` · ${c.calf_tag_no}` : ''}${c.calf_name ? ` · ${c.calf_name}` : ''}`,
          _supplierName: (c.supplier_id && supplierName.get(c.supplier_id)) || '—',
        })),
      ];
      const term = (params?.search || '').toLowerCase().trim();
      const filtered = term ? merged.filter((r) => JSON.stringify(r).toLowerCase().includes(term)) : merged;
      filtered.sort((a, b) => (a.purchase_date < b.purchase_date ? 1 : a.purchase_date > b.purchase_date ? -1 : 0));
      const page = params?.page ?? 1;
      const pageSize = params?.page_size ?? 10;
      const start = (page - 1) * pageSize;
      return { items: filtered.slice(start, start + pageSize), total: filtered.length, page, page_size: pageSize };
    },
    create: (v: any) => {
      if (v.purchase_type === 'Calf') {
        return calfRecordsApi.create({
          source: 'Purchased', calf_gender: v.calf_gender, birth_date: v.birth_date || v.purchase_date,
          purchase_date: v.purchase_date, purchase_cost: Number(v.calf_cost || 0), supplier_id: v.supplier_id || undefined,
          calf_tag_no: v.calf_tag_no || undefined, calf_name: v.calf_name || undefined, remarks: v.notes || undefined,
        } as any) as unknown as Promise<UnifiedPurchase>;
      }
      if (v.purchase_type === 'Animal') {
        // animalPurchasesApi has no separate Create DTO type (unlike
        // Purchase/PurchaseCreate) — it reuses the Read type's field types
        // (DecimalString) even for writes, so a plain numeric payload needs
        // an `any` cast here; the backend accepts a JSON number either way.
        return animalPurchasesApi.create({
          animal_id: v.animal_id, supplier_id: v.supplier_id || undefined,
          purchase_date: v.purchase_date, purchase_cost: Number(v.purchase_cost),
          transport_cost: v.transport_cost !== '' && v.transport_cost != null ? Number(v.transport_cost) : undefined,
          insurance_cost: v.insurance_cost !== '' && v.insurance_cost != null ? Number(v.insurance_cost) : undefined,
          invoice_number: v.invoice_number || undefined,
          notes: v.notes || undefined,
        } as any) as unknown as Promise<UnifiedPurchase>;
      }
      return purchasesApi.create({
        farm_id: v.farm_id, purchase_date: v.purchase_date, supplier_id: v.supplier_id,
        purchase_type: v.purchase_type, invoice_number: v.invoice_number || undefined,
        tax: v.tax ? Number(v.tax) : undefined,
        receiving_location_id: v.receiving_location_id || undefined,
        items: [{
          item_type: v.purchase_type, item_ref_id: v.item_ref_id || undefined,
          description: v.description, quantity: Number(v.quantity), unit_price: Number(v.unit_price),
        }],
      }) as unknown as Promise<UnifiedPurchase>;
    },
    update: (id: string, v: any) => {
      if (v.kind === 'CalfPurchase') {
        return calfRecordsApi.update(id, {
          calf_gender: v.calf_gender, birth_date: v.birth_date, purchase_date: v.purchase_date,
          purchase_cost: Number(v.calf_cost || 0), supplier_id: v.supplier_id || null,
          calf_tag_no: v.calf_tag_no || null, calf_name: v.calf_name || null, remarks: v.notes || null,
        } as any) as unknown as Promise<UnifiedPurchase>;
      }
      if (v.kind === 'AnimalPurchase') {
        return animalPurchasesApi.update(id, {
          supplier_id: v.supplier_id || undefined, purchase_date: v.purchase_date,
          purchase_cost: v.purchase_cost !== '' && v.purchase_cost != null ? Number(v.purchase_cost) : undefined,
          transport_cost: v.transport_cost !== '' && v.transport_cost != null ? Number(v.transport_cost) : undefined,
          insurance_cost: v.insurance_cost !== '' && v.insurance_cost != null ? Number(v.insurance_cost) : undefined,
          invoice_number: v.invoice_number || undefined, notes: v.notes || undefined,
          return_date: v.return_date || undefined, return_reason: v.return_reason || undefined,
        } as any) as unknown as Promise<UnifiedPurchase>;
      }
      return purchasesApi.updatePaymentStatus(id, v.payment_status) as unknown as Promise<UnifiedPurchase>;
    },
  },
  idKey: 'id',
  defaultValues: { purchase_date: today(), purchase_type: 'Feed' } as unknown as Partial<UnifiedPurchase>,
  // A bought calf keeps its cost and notes in its Calf Record's own fields.
  deriveEditValues: (row) => (row.kind === 'CalfPurchase' ? { calf_cost: row.purchase_cost != null ? Number(row.purchase_cost) : '', notes: row.remarks ?? '' } : {}),
  columns: [
    { key: 'date', header: 'Date', primary: true, render: (r) => formatDate(r.purchase_date) },
    { key: 'type', header: 'Type', render: (r) => r.purchase_type },
    { key: 'reference', header: 'Animal / Item', render: (r) => r._reference },
    { key: 'supplier', header: 'Supplier', render: (r) => r._supplierName, hideOnTablet: true },
    { key: 'total', header: 'Total', align: 'right', render: (r) => (
      r.kind === 'CalfPurchase'
        ? (Number(r.purchase_cost || 0) === 0 ? 'Free' : formatCurrency(r.purchase_cost))
        : formatCurrency(
          r.kind === 'AnimalPurchase'
            ? Number(r.purchase_cost || 0) + Number(r.transport_cost || 0) + Number(r.insurance_cost || 0)
            : r.total,
        )
    ) },
    { key: 'status', header: 'Status', render: (r) => (r.kind === 'CalfPurchase'
      ? <ILMSStatusBadge status={r.status === 'OnFarm' ? 'Active' : r.status} size="sm" />
      : r.kind === 'AnimalPurchase'
        ? (r.return_date ? <ILMSStatusBadge status="Returned" size="sm" /> : <ILMSStatusBadge status="Active" size="sm" />)
        : <ILMSStatusBadge status={r.payment_status} size="sm" />) },
  ],
  fields: [
    { name: 'farm_id', label: 'Farm', type: 'select', section: 'Purchase', required: isNotAnimalPurchase, visibleWhen: isNotAnimalPurchase, optionsKey: 'farms' },
    { name: 'supplier_id', label: 'Supplier', type: 'select', section: 'Purchase', required: isNotAnimalPurchase, optional: true, optionsKey: 'suppliers' },
    { name: 'purchase_date', label: 'Date', type: 'date', section: 'Purchase', required: true },
    { name: 'purchase_type', label: 'Type', type: 'select', section: 'Purchase', required: true, staticOptions: [
      { value: 'Animal', label: 'Animal' }, { value: 'Calf', label: 'Calf' }, { value: 'Feed', label: 'Feed' }, { value: 'Medicine', label: 'Medicine' },
      { value: 'Equipment', label: 'Equipment' }, { value: 'Other', label: 'Other' },
    ], helperText: 'Animal = an adult cow already added under Animals. Calf = a young calf, free or at a cost - it goes to Dairy → Calf Records.' },
    { name: 'invoice_number', label: 'Invoice Number', type: 'text', section: 'Purchase', optional: true },

    // Feed/Medicine/Equipment/Other — the line-item side of a generic Purchase.
    { name: 'item_ref_id', label: 'Item', type: 'select', section: 'Item', optional: true, optionsKey: purchaseItemRefOptionsKey,
      visibleWhen: (v) => v.purchase_type === 'Feed' || v.purchase_type === 'Medicine',
      helperText: 'Which Feed Item / Medicine this is — this is what actually adds it to Inventory stock on receipt.' },
    { name: 'receiving_location_id', label: 'Receiving Location', type: 'select', section: 'Item', optional: true, optionsKey: 'inventoryLocations',
      visibleWhen: (v) => ['Feed', 'Medicine', 'Equipment'].includes(v.purchase_type as string),
      helperText: 'Which stock location this delivery is going into — required for Feed/Medicine/Equipment to receive it into Inventory.' },
    { name: 'description', label: 'Description', type: 'text', section: 'Item', required: isCreatingNonAnimalPurchase, visibleWhen: isNotAnimalPurchase },
    { name: 'quantity', label: 'Quantity', type: 'decimal', section: 'Item', required: isCreatingNonAnimalPurchase, visibleWhen: isNotAnimalPurchase, min: 0 },
    { name: 'unit_price', label: 'Unit Price', type: 'decimal', section: 'Item', required: isCreatingNonAnimalPurchase, visibleWhen: isNotAnimalPurchase, min: 0 },
    { name: 'tax', label: 'Tax', type: 'decimal', section: 'Item', optional: true, visibleWhen: isNotAnimalPurchase, min: 0 },
    { name: 'payment_status', label: 'Payment Status', type: 'select', section: 'Payment', optional: true, visibleWhen: isNotAnimalPurchase, staticOptions: [
      { value: 'Unpaid', label: 'Unpaid' }, { value: 'Partial', label: 'Partial' }, { value: 'Paid', label: 'Paid' },
    ] },

    // Animal — the same fields the old standalone Animal Purchases tab had.
    // The picker only lists animals that already exist — the Animal record
    // itself (tags, breed, photos...) is a different kind of data entirely
    // and is created on its own screen, not duplicated into this form.
    // "+ Add new animal" sends the user there and back again with the new
    // animal ready to select, instead of leaving them stuck if it's not
    // in the system yet.
    { name: 'animal_id', label: 'Animal', type: 'select', section: 'Animal', required: isAnimalPurchase, visibleWhen: isAnimalPurchase, optionsKey: 'animals',
      actionLabel: '+ Add new animal', onAction: () => { window.location.assign('/animals/new?returnTo=purchases'); } },
    { name: 'purchase_cost', label: 'Purchase Cost', type: 'decimal', section: 'Animal', required: isAnimalPurchase, visibleWhen: isAnimalPurchase, min: 0,
      helperText: 'Saved as this animal’s Acquisition Cost on its own profile automatically — no need to enter it again there.' },
    { name: 'transport_cost', label: 'Transport Cost', type: 'decimal', section: 'Animal', optional: true, visibleWhen: isAnimalPurchase, min: 0 },
    { name: 'insurance_cost', label: 'Insurance Cost', type: 'decimal', section: 'Animal', optional: true, visibleWhen: isAnimalPurchase, min: 0 },
    // Calf - stored as a bought Calf Record (Dairy → Calf Records).
    { name: 'calf_gender', label: 'Calf — Male or Female', type: 'select', section: 'Calf', required: isCalfPurchase, visibleWhen: isCalfPurchase,
      staticOptions: [{ value: 'Female', label: 'Female' }, { value: 'Male', label: 'Male' }] },
    { name: 'calf_cost', label: 'Cost (₹)', type: 'decimal', section: 'Calf', required: isCalfPurchase, visibleWhen: isCalfPurchase, min: 0,
      helperText: 'Enter 0 if the calf came free.' },
    { name: 'birth_date', label: 'Calf’s Birth Date', type: 'date', section: 'Calf', optional: true, visibleWhen: isCalfPurchase,
      helperText: 'Roughly is fine. Left blank, the purchase date is used.' },
    { name: 'calf_tag_no', label: 'Calf Tag No', type: 'text', section: 'Calf', optional: true, visibleWhen: isCalfPurchase },
    { name: 'calf_name', label: 'Calf Name', type: 'text', section: 'Calf', optional: true, visibleWhen: isCalfPurchase },
    { name: 'notes', label: 'Notes', type: 'textarea', section: 'Notes', optional: true, visibleWhen: (v) => isAnimalPurchase(v) || isCalfPurchase(v), fullWidth: true },
    { name: 'return_date', label: 'Return Date', type: 'date', section: 'Return to Supplier', optional: true, visibleWhen: isAnimalPurchase,
      helperText: 'Fill in only if this purchase was sent back to the supplier.' },
    { name: 'return_reason', label: 'Return Reason', type: 'textarea', section: 'Return to Supplier', optional: true, visibleWhen: isAnimalPurchase, fullWidth: true },
  ] as FieldConfig<any>[],
};

const salesConfig: ModuleConfig<Sale> = {
  title: 'Sales', entityLabel: 'sale', module: 'Sales', queryKey: 'sales',
  subtitle: 'Egg, Animal, Wool, Manure, Compost and Coconut sales — milk sold to a Partner/Vendor/Customer is recorded under Dairy → Milk Sales instead.',
  referenceKeys: ['farms', 'customers'],
  canDelete: true,
  api: {
    list: salesApi.list,
    create: (v: any) => salesApi.create({
      farm_id: v.farm_id, sale_date: v.sale_date, sale_type: v.sale_type, customer_id: v.customer_id || undefined,
      tax: v.tax ? Number(v.tax) : undefined,
      items: [{ item_type: v.sale_type, description: v.description, quantity: Number(v.quantity), unit_price: Number(v.unit_price) }],
    }),
    update: (id: string, v: any) => salesApi.updatePaymentStatus(id, v.payment_status),
    remove: salesApi.remove,
  },
  idKey: 'id',
  defaultValues: { sale_date: today(), sale_type: 'Egg' },
  columns: [
    { key: 'date', header: 'Date', primary: true, render: (r) => formatDate(r.sale_date) },
    { key: 'type', header: 'Type', render: (r) => r.sale_type },
    { key: 'total', header: 'Total', align: 'right', render: (r) => formatCurrency(r.total) },
    { key: 'status', header: 'Payment', render: (r) => <ILMSStatusBadge status={r.payment_status} size="sm" /> },
  ],
  fields: [
    { name: 'farm_id', label: 'Farm', type: 'select', section: 'Sale', required: true, optionsKey: 'farms' },
    { name: 'sale_date', label: 'Date', type: 'date', section: 'Sale', required: true },
    // Milk deliberately excluded here - record that under Dairy -> Milk
    // Sales instead (its own litres/fat%/rate fields, same approval gate),
    // so there's exactly one place milk sales get entered, not two.
    { name: 'sale_type', label: 'Type', type: 'select', section: 'Sale', required: true, staticOptions: [
      { value: 'Egg', label: 'Egg' }, { value: 'Animal', label: 'Animal' },
      { value: 'Wool', label: 'Wool' }, { value: 'Manure', label: 'Manure' }, { value: 'Compost', label: 'Compost' },
      { value: 'Coconut', label: 'Coconut' }, { value: 'Other', label: 'Other' },
    ] },
    { name: 'customer_id', label: 'Customer', type: 'select', section: 'Sale', optional: true, optionsKey: 'customers' },
    { name: 'description', label: 'Description', type: 'text', section: 'Item', required: true },
    { name: 'quantity', label: 'Quantity', type: 'decimal', section: 'Item', required: true, min: 0 },
    { name: 'unit_price', label: 'Unit Price', type: 'decimal', section: 'Item', required: true, min: 0 },
    { name: 'tax', label: 'Tax', type: 'decimal', section: 'Item', optional: true, min: 0 },
    { name: 'payment_status', label: 'Payment Status', type: 'select', section: 'Payment', optional: true, staticOptions: [
      { value: 'Unpaid', label: 'Unpaid' }, { value: 'Partial', label: 'Partial' }, { value: 'Paid', label: 'Paid' },
    ] },
  ] as FieldConfig<any>[],
};

const expensesConfig: ModuleConfig<Expense> = {
  title: 'Expenses', entityLabel: 'expense', module: 'Expenses', queryKey: 'expenses',
  referenceKeys: ['farms', 'expenseCategories', 'suppliers'],
  api: {
    list: expensesApi.list,
    create: expensesApi.create,
    update: (id: string, v: any) => (v.approval_status ? expensesApi.updateApproval(id, v.approval_status) : expensesApi.update(id, v)),
  },
  idKey: 'id',
  defaultValues: { expense_date: undefined },
  columns: [
    { key: 'date', header: 'Date', primary: true, render: (r) => formatDate(r.expense_date) },
    { key: 'description', header: 'Description', render: (r) => r.description },
    { key: 'amount', header: 'Amount', align: 'right', render: (r) => formatCurrency(r.amount) },
    { key: 'approval', header: 'Approval', render: (r) => <ILMSStatusBadge status={r.approval_status} size="sm" /> },
  ],
  fields: [
    { name: 'farm_id', label: 'Farm', type: 'select', section: 'Expense', required: true, optionsKey: 'farms' },
    { name: 'category_id', label: 'Category', type: 'select', section: 'Expense', required: true, optionsKey: 'expenseCategories' },
    { name: 'expense_date', label: 'Date', type: 'date', section: 'Expense', required: true },
    { name: 'description', label: 'Description', type: 'text', section: 'Expense', required: true },
    { name: 'amount', label: 'Amount', type: 'decimal', section: 'Expense', required: true, min: 0 },
    { name: 'payment_mode', label: 'Payment Mode', type: 'select', section: 'Payment', required: true, staticOptions: [
      { value: 'Cash', label: 'Cash' }, { value: 'Bank', label: 'Bank' }, { value: 'UPI', label: 'UPI' },
      { value: 'Cheque', label: 'Cheque' }, { value: 'Card', label: 'Card' },
    ] },
    { name: 'supplier_id', label: 'Vendor', type: 'select', section: 'Payment', optional: true, optionsKey: 'suppliers' },
    { name: 'reference_number', label: 'Reference Number', type: 'text', section: 'Payment', optional: true },
    { name: 'approval_status', label: 'Approval Status', type: 'select', section: 'Approval', optional: true, staticOptions: [
      { value: 'Pending', label: 'Pending' }, { value: 'Approved', label: 'Approved' }, { value: 'Rejected', label: 'Rejected' },
    ] },
  ],
};

const expenseCategoriesConfig: ModuleConfig<ExpenseCategory> = {
  title: 'Expense Categories', entityLabel: 'category', module: 'Expenses', queryKey: 'expense-categories', api: expenseCategoriesApi, idKey: 'id',
  defaultValues: { active: true },
  columns: [
    { key: 'name', header: 'Name', primary: true, render: (r) => r.name },
    { key: 'active', header: 'Status', render: (r) => activeBadge(r.active) },
  ],
  fields: [
    { name: 'name', label: 'Name', type: 'text', section: 'Details', required: true },
    { name: 'active', label: 'Active', type: 'checkbox', section: 'Details' },
  ],
};

export default function ProcurementFinancePage() {
  // Reached from Add Animal's "returnTo=purchases" redirect (see
  // AnimalForm.tsx) with ?tab=purchases&prefill_animal=<id> — lands
  // directly on Purchases with Add Purchase already open and that animal
  // selected, so creating a Purchased animal and logging its cost is one
  // continuous trip across the two screens rather than two unrelated ones.
  const [searchParams] = useSearchParams();
  const tab = searchParams.get('tab') ?? undefined;
  const prefillAnimalId = searchParams.get('prefill_animal');

  return (
    <TabbedPage
      title="Procurement & Finance"
      subtitle="Suppliers, purchases (animals and calves included), expenses and other sales (eggs, coconut…). Milk customers, their plans and payments are under Online Shop & Customers."
      defaultTab={tab}
      tabs={[
        { id: 'suppliers', label: 'Suppliers', render: () => <ResourceModule config={suppliersConfig} /> },
        { id: 'purchase-orders', label: 'Purchase Orders', render: () => <ResourceModule config={purchaseOrdersConfig} /> },
        {
          id: 'purchases', label: 'Purchases',
          render: () => (
            <ResourceModule
              config={purchasesConfig}
              autoOpenAdd={prefillAnimalId ? { purchase_type: 'Animal', animal_id: prefillAnimalId } : undefined}
            />
          ),
        },
        { id: 'expense-categories', label: 'Expense Categories', render: () => <ResourceModule config={expenseCategoriesConfig} /> },
        { id: 'expenses', label: 'Expenses', render: () => <ResourceModule config={expensesConfig} /> },
        { id: 'sales', label: 'Other Sales', render: () => <ResourceModule config={salesConfig} /> },
      ]}
    />
  );
}

// For Administration -> Entry Form Settings.
registerForms('Procurement & Finance', suppliersConfig, purchaseOrdersConfig, purchasesConfig, expenseCategoriesConfig, expensesConfig, salesConfig);
